import { useHideBackdrop } from '@/hooks/useHideBackdrop';
import { Pixels } from '@/themes/variant-interfaces/Pixels';
import { SXStyles } from '@/themes/variant-interfaces/SXStyles';
import Close from '@mui/icons-material/Close';
import {
  Box,
  Dialog as MuiDialog,
  DialogActions,
  DialogContent,
  DialogProps as MuiDialogProps,
  DialogTitle,
  Stack,
  SxProps,
} from '@mui/material';
import { ReactNode } from 'react';
import { ButtonProps } from '../Button/Button';
import { HSpacer, VSpacer } from '../Spacer';
import { Text, TextProps } from '../Text/Text';

export interface DialogProps {
  acceptButton?: (props: ButtonProps) => ReactNode,
  cancelButton?: (props: ButtonProps) => ReactNode,
  children?: ReactNode,
  contentSx?: SxProps,
  dialogWidth?: Pixels,
  fullScreen?: boolean,
  fullWidth?: boolean,
  heroIcon?: ReactNode,
  isTitleCentered?: boolean,
  loading?: boolean,
  maxWidth?: MuiDialogProps['maxWidth'],
  onClose: () => void,
  open?: boolean,
  px?: number | string,
  secondaryActionButton?: (props: ButtonProps) => ReactNode,
  showCloseButton?: boolean,
  showTitle?: boolean,
  subtitle?: string | ReactNode,
  testID: string,
  title?: string | ReactNode,
  titleProps?: TextProps,
  sx?: SxProps,
}

const positionTitle = (isCentered: boolean) => (isCentered ? 'center' : 'left');

export const Dialog = ({
  acceptButton,
  cancelButton,
  children,
  contentSx,
  dialogWidth = '560px',
  fullScreen = false,
  fullWidth = true,
  heroIcon,
  isTitleCentered = false,
  open = false,
  px,
  loading = false,
  maxWidth = 'xs',
  onClose,
  secondaryActionButton,
  showCloseButton = true,
  showTitle = true,
  subtitle,
  testID,
  title,
  titleProps,
  sx,
}: DialogProps) => {
  const AcceptButtonProps: ButtonProps = {
    loading,
    testID: `${testID}-accept`,
    variant: "contained",
  };
  const CancelButtonProps: ButtonProps = {
    disabled: loading,
    testID: `${testID}-cancel`,
    variant: "text",
  };
  const SecondaryActionButtonProps: ButtonProps = {
    disabled: loading,
    testID: `${testID}-secondary-action`,
    variant: "text",
  };

  const styles: SXStyles = {
    closeButton: {
      cursor: 'pointer',
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      position: 'absolute',
      right: 13,
      top: 13,
      width: 48,
      height: 48,
    },
  };

  const { hide: hideBackdrop, transitionDuration } = useHideBackdrop(open);
  const backdropStyle = {
    backdropFilter: hideBackdrop ? 'none' : 'blur(5px)',
    opacity: hideBackdrop ? 0 : 1,
    pointerEvents: hideBackdrop ? 'none' : undefined,
    transitionDuration,
    visibility: hideBackdrop ? 'hidden' : 'visible',
  } as const;

  return (
    <MuiDialog
      PaperProps={{ sx: { maxWidth: dialogWidth, ...sx } }}
      componentsProps={{
        backdrop: {
          style: backdropStyle,
        },
      }}
      data-testid={testID}
      fullScreen={fullScreen}
      fullWidth={fullWidth}
      maxWidth={maxWidth}
      onClose={onClose}
      open={open}
    >
      {showCloseButton && (
        <Box
          data-testid={`${testID}-close`}
          onClick={onClose}
          sx={styles.closeButton}
        >
          <Close sx={{ width: 22.6, height: 22.6 }} />
        </Box>
      )}
      {heroIcon && (
        <>
          <VSpacer size="7" />
          <Stack alignItems="center">
            <DialogContent data-testid={`${testID}-hero-icon`} sx={{ p: '0' }}>
              {heroIcon}
            </DialogContent>
          </Stack>
        </>
      )}
      {showTitle && (
        <DialogTitle
          data-testid={`${testID}-title`}
          fontSize={24}
          sx={{
            p: '24px 24px 16px 24px',
            textAlign: positionTitle(isTitleCentered || !!heroIcon),
          }}
        >
          {typeof title === 'string'
            ? (
              <Text category="headline-small" sx={{ overflowWrap: 'break-word' }} {...titleProps}>
                {title}
              </Text>
            )
            : title
          }
        </DialogTitle>
      )}
      {!!subtitle && (
        <DialogTitle
          data-testid={`${testID}-subtitle`}
          fontSize={14}
          sx={{
            p: '0 24px 16px',
            textAlign: positionTitle(isTitleCentered || !!heroIcon),
          }}
        >
          {typeof subtitle === 'string' ? (
            <Text
              category="body-medium"
              sx={{ overflowWrap: 'break-word' }}
              {...titleProps}
            >
              {subtitle}
            </Text>
          ) : subtitle}
        </DialogTitle>
      )}
      <DialogContent data-testid={`${testID}-content`} sx={{ pb: 0, px, ...contentSx }}>
        {children}
      </DialogContent>
      {(secondaryActionButton?.(SecondaryActionButtonProps)
        || cancelButton?.(CancelButtonProps)
        || acceptButton?.(AcceptButtonProps)
      ) && (
        <DialogActions sx={{
          justifyContent: !secondaryActionButton ? "flex-end" : "space-between",
          paddingY: (cancelButton || acceptButton) ? '24px' : 0,
        }}>
          {secondaryActionButton?.(SecondaryActionButtonProps)}
          <Stack direction="row">
            {cancelButton?.(CancelButtonProps)}
            <HSpacer size="3" />
            {acceptButton?.(AcceptButtonProps)}
          </Stack>
        </DialogActions>
      )}
    </MuiDialog>
  );
};
